import { useState } from "react";
import FieldRenderer from "../FieldRenderer";
import GridContainer from "components/common/GridContainer";
import GridItem from "components/common/GridItem";
import menu from "config/menu";
import DText from "components/common/DText";
import Checkbox from "@mui/material/Checkbox";

// Helper: Collect ALL permissions under an item (including deep nested children)
const getAllPermissions = (item) => {
    let perms = [...(item.requiredPermissions || [])];
    if (item.children?.length) {
        item.children.forEach((child) => {
            perms = perms.concat(getAllPermissions(child));
        });
    }
    return Array.from(new Set(perms));
};

// Helper: Find item and its ancestor path in the tree
const findParentChain = (items, targetId, currentPath = []) => {
    for (const item of items) {
        if (item.id === targetId) return currentPath;
        if (item.children?.length) {
            const result = findParentChain(item.children, targetId, [
                ...currentPath,
                item
            ]);
            if (result) return result;
        }
    }
    return null;
};

export default function UserGroupForm({
    resource,
    form,
    setForm,
    showValidation
}) {
    const [errors, setErrors] = useState({});

    // Check if permissions are active
    const checkPermission = (requiredPermissions) => {
        if (!form?.permissions?.length || !requiredPermissions?.length)
            return false;

        const activePermissions = form.permissions
            .filter((item) => !item.isDeleted)
            .map((item) => item.permission);

        // Active if ANY required permission is active
        return requiredPermissions.some((permission) =>
            activePermissions.includes(permission)
        );
    };

    function onCheck(checked, item) {
        setForm((prevForm) => {
            let updatedPermissions = prevForm?.permissions
                ? [...prevForm.permissions]
                : [];

            const updatePermissionKey = (permissionKey, isChecked) => {
                const existingIndex = updatedPermissions.findIndex(
                    (p) => p.permission === permissionKey
                );

                if (isChecked) {
                    if (existingIndex !== -1) {
                        if (updatedPermissions[existingIndex].id) {
                            updatedPermissions[existingIndex] = {
                                ...updatedPermissions[existingIndex],
                                isDeleted: false
                            };
                        }
                    } else {
                        updatedPermissions.push({ permission: permissionKey });
                    }
                } else {
                    if (existingIndex !== -1) {
                        const existing = updatedPermissions[existingIndex];
                        if (existing.id) {
                            updatedPermissions[existingIndex] = {
                                ...existing,
                                isDeleted: true
                            };
                        } else {
                            updatedPermissions.splice(existingIndex, 1);
                        }
                    }
                }
            };

            // 1. Cascade down: Toggle target item and ALL its downstream descendants
            const allTargetPermissions = getAllPermissions(item);
            allTargetPermissions.forEach((permKey) =>
                updatePermissionKey(permKey, checked)
            );

            // 2. Cascade up: Re-evaluate parent chain bottom-up
            const parents = findParentChain(menu, item.id);
            if (parents && parents.length > 0) {
                // Reverse to work bottom-up
                [...parents].reverse().forEach((parent) => {
                    const activeSet = new Set(
                        updatedPermissions
                            .filter((p) => !p.isDeleted)
                            .map((p) => p.permission)
                    );

                    const childPermissions = parent.children.flatMap((child) =>
                        getAllPermissions(child)
                    );

                    const anyActive = childPermissions.some((perm) =>
                        activeSet.has(perm)
                    );

                    // Sync parent permission based on active children state
                    parent.requiredPermissions.forEach((permKey) => {
                        updatePermissionKey(permKey, anyActive);
                    });
                });
            }

            return { ...prevForm, permissions: updatedPermissions };
        });
    }

    const renderCheckbox = (item) => {
        const itemAllPermissions = getAllPermissions(item);
        return (
            <GridItem key={item.id}>
                <Checkbox
                    sx={{ padding: "0px" }}
                    checked={checkPermission(itemAllPermissions)}
                    onChange={(event) => {
                        onCheck(event.target.checked, item);
                    }}
                />
            </GridItem>
        );
    };

    // Recursive component renderer
    const renderPermissionItem = (item, level = 0) => {
        const hasChildren = item.children && item.children.length > 0;

        return (
            <GridItem
                key={item.id}
                xs={level === 0 ? 12 : 12 / Math.min(level, 2)}
                // sx={{ width: "100%" }}
            >
                <GridContainer direction="column" spacing={1}>
                    <GridItem>
                        <GridContainer noWrap alignItems="center" spacing={1}>
                            {renderCheckbox(item)}
                            <GridItem>
                                <DText
                                    bolder={level === 0}
                                    md={level === 0}
                                    sm={level > 0}
                                >
                                    {item.name}
                                </DText>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    {/* Recursive render for children */}
                    {hasChildren && (
                        <GridItem sx={{ pl: level * 2 }}>
                            <GridContainer
                                direction={level === 0 ? "row" : "column1"}
                                spacing={1}
                                card={level === 0}
                                sx={
                                    level !== 0
                                        ? {
                                              border: "solid 1px #ccc",
                                              borderRadius: "4px",
                                              padding: "8px"
                                          }
                                        : {}
                                }
                            >
                                {item.children.map((child) =>
                                    renderPermissionItem(child, level + 1)
                                )}
                            </GridContainer>
                        </GridItem>
                    )}
                </GridContainer>
            </GridItem>
        );
    };

    return (
        <GridContainer direction="column" spacing={2}>
            {resource.fields.map((field) => (
                <GridItem key={field.name} xs={12}>
                    <FieldRenderer
                        key={field.name}
                        field={field}
                        form={form}
                        setForm={setForm}
                        errors={errors}
                        showValidation={showValidation}
                    />
                </GridItem>
            ))}

            <GridItem xs={12}>
                <DText bolder blue md>
                    Permissions
                </DText>
            </GridItem>

            <GridItem xs={12}>
                <GridContainer direction="column" spacing={2}>
                    {menu
                        .filter((o) => o.requiredPermissions?.[0] !== "all")
                        .map((item) => renderPermissionItem(item, 0))}
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
}
